import React, { useState, useEffect, createRef } from "react";
// No longer using react-table, removed unused imports
// Removed unused LoadingSpinner import
import { Button, Row, Col, InputGroup, Form, Breadcrumb, Card, Container } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import { useTheme } from '@repo/shared';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash, faFolder, faFile } from '@fortawesome/free-solid-svg-icons'
import ImagesDataService from "../../services/image/ImageService";
import UploadFiles from './UploadFiles';

// No longer using COLS_PER_ROW, grid will be responsive
const VALIDATE_TOKEN = 'f6fb4928afe247afbb7fd4549d0505ae';

/**
 * Modernized ImageOrganiser component.
 * Uses react-bootstrap for layout and a responsive CSS grid for file browsing.
 */
const ImageOrganiser = (props) => {
  const { user } = props;
  const history = useHistory();
  const location = useLocation();
  console.log('logged in as', user);
  const { appTheme } = useTheme();

  // Get initial path from URL query parameter, default to empty string
  const getPathFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('path') || '';
  };

  const [currentPath, setCurrentPath] = useState(getPathFromUrl());
  const [contents, setContents] = useState([]);
  const [newFolderName, setNewFolderName] = useState("");

  const uploadComponent = createRef();

  // Sync currentPath with URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pathFromUrl = params.get('path') || '';
    if (pathFromUrl !== currentPath) {
      setCurrentPath(pathFromUrl);
    }
  }, [location]);

  useEffect(() => {
    document.title = `${appTheme?.appName} - Image Organiser`
    updateContents();
  }, [currentPath]);

  const updateContents = async () => {
    const newContents = await ImagesDataService.getForPath(currentPath);
    const transformedNewContents = newContents.map(c => {
      //Remove the path from filenames
      return { ...c, name: c.name.replace(/^.*[\\\/]/, '') }
    }).sort((a, b) => {
      //Dirs come first
      if (a.isDir) {
        if (!b.isDir) {
          return -1;
        }
      } else if (b.isDir) {
        return 1;
      }

      return a.name.localeCompare(b.name);
    })
    setContents(transformedNewContents);
  }

  const addFolder = async () => {
    if (!newFolderName) return; // Don't add empty folder
    console.log('add folder', currentPath, newFolderName)
    try {
      const newContents = await ImagesDataService.createDir({ path: currentPath, name: newFolderName });
      setContents(newContents);
      setNewFolderName(""); // Clear input after success
    } catch (error) {
      console.error("Failed to add folder:", error);
      // You could set an error message state here
    }
  };

  // This function is not used in the UI, but mechanism is retained
  const goUp = () => {
    const pathParts = currentPath.split('/');
    if (pathParts.length === 2) {
      setCurrentPath('');
    } else {
      pathParts.splice(pathParts.length - 2, 2)
      const newPath = pathParts.join('/') + '/';
      setCurrentPath(newPath);
    }
  }

  const browseToFolder = (toFolder) => {
    // Push the new path to history to enable back button
    const params = new URLSearchParams();
    if (toFolder) {
      params.set('path', toFolder);
    }
    history.push(`?${params.toString()}`);

    if (uploadComponent.current) {
      uploadComponent.current.reset();
    }
  }

  const confirmDeleteItem = async (item) => {
    // Using a simple confirm. For a more modern look, you could
    // replace this with a custom modal (like react-confirm-alert from Class.js)
    if (window.confirm(`Are you sure you want to delete ${item.name}?`)) {
      try {
        const newContents = await ImagesDataService.deleteFile({ path: currentPath, filename: item.name + (item.isDir ? '/' : '') });
        setContents(newContents);
      } catch (error) {
        console.error("Failed to delete item:", error);
      }
    }
  }

  /**
   * Renders a single File or Folder item for the grid.
   */
  const ContentItem = ({ content }) => {
    const isImage = (['.png', '.jpg', '.gif', '.jpeg'].some(suffix => content.name.toLowerCase().endsWith(suffix)));

    // Card styling
    const itemStyle = {
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      padding: '1rem',
      textAlign: 'center',
      position: 'relative',
      backgroundColor: '#fff',
      transition: 'box-shadow 0.2s ease-in-out',
      wordBreak: 'break-word',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      minHeight: '180px', // Ensure consistent height
    };

    // Icon container
    const iconContainerStyle = {
      fontSize: '4rem',
      color: '#6c757d',
      marginBottom: '0.75rem',
      flexGrow: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    // Delete button
    const deleteBtnStyle = {
      position: 'absolute',
      top: '8px',
      right: '8px',
      cursor: 'pointer',
      color: '#dc3545',
      backgroundColor: 'white',
      borderRadius: '50%',
      padding: '2px 6px',
      lineHeight: '1',
      fontSize: '0.8rem',
    };

    // Name text
    const textStyle = {
      fontSize: '0.9rem',
      fontWeight: '500',
    };

    const renderIcon = () => {
      if (content.isDir) {
        return (
          <div style={iconContainerStyle} onClick={() => browseToFolder(content.url)} role="button" title={`Open folder ${content.name}`}>
            <FontAwesomeIcon icon={faFolder} style={{ color: '#0d6efd' }} />
          </div>
        );
      }

      if (isImage) {
        return (
          <div style={iconContainerStyle}>
            <a href={`${content.url}?validate=${VALIDATE_TOKEN}`} target="_blank" rel="noopener noreferrer" title={`View image ${content.name}`}>
              <img
                src={`${content.url}?validate=${VALIDATE_TOKEN}`}
                style={{ maxWidth: '100%', maxHeight: '6rem', height: 'auto', borderRadius: '4px' }}
                alt={content.name}
              />
            </a>
          </div>
        );
      }

      // Default file icon
      return (
        <div style={iconContainerStyle}>
          <a href={`${content.url}?validate=${VALIDATE_TOKEN}`} target="_blank" rel="noopener noreferrer" title={`Open file ${content.name}`}>
            <FontAwesomeIcon icon={faFile} />
          </a>
        </div>
      );
    };

    return (
      <div style={itemStyle} className="content-item-card">
        {/* Add hover effect via CSS if preferred */}
        <style>{`.content-item-card:hover { box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); }`}</style>

        {(user.role === 'artist' || user.username === 'lee') && (
          <span style={deleteBtnStyle} onClick={() => confirmDeleteItem(content)} title={`Delete ${content.name}`}>
            <FontAwesomeIcon icon={faTrash} />
          </span>
        )}

        {renderIcon()}

        <div style={textStyle} title={content.name}>
          {content.isDir ? (
            <span onClick={() => browseToFolder(content.url)} role="button" style={{ color: '#0d6efd', textDecoration: 'none' }}>
              {content.name}
            </span>
          ) : (
            <span>{content.name}</span>
          )}
        </div>
      </div>
    );
  };

  /**
   * Renders the file/folder grid.
   */
  const renderBody = () => {
    // Modern, responsive CSS grid
    const gridStyle = {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: '1.5rem',
      marginTop: '1.5rem',
    };

    return (
      <div style={gridStyle}>
        {contents.map(content => (
          <ContentItem key={content.name} content={content} />
        ))}
        {contents.length === 0 && (
          <p style={{ gridColumn: '1 / -1', color: '#6c757d' }}>This folder is empty.</p>
        )}
      </div>
    );
  }

  /**
   * Renders the path as breadcrumbs.
   */
  const renderCurrentPath = () => {
    const pathParts = currentPath.replace(/\/$/, '').split('/');

    return (
      <Breadcrumb listProps={{ style: { marginBottom: 0, backgroundColor: '#f8f9fa', padding: '0.75rem 1rem', borderRadius: '8px' } }}>
        <Breadcrumb.Item onClick={() => browseToFolder('')} active={currentPath === ''}>
          Start
        </Breadcrumb.Item>

        {currentPath !== '' && pathParts.map((p, index) => {
          const pathToNow = pathParts.slice(0, index + 1).join('/') + '/';
          const isLast = index === pathParts.length - 1;

          return (
            <Breadcrumb.Item
              key={index}
              onClick={() => !isLast && browseToFolder(pathToNow)}
              active={isLast}
            >
              {p}
            </Breadcrumb.Item>
          );
        })}
      </Breadcrumb>
    );
  };

  return (
    // STYLING: Main container for padding (matches Class.js)
    <Container className="py-2 py-lg-4">
      <Card>
        <Card.Header className="d-flex align-items-center gap-2">
          <h2 className="mb-0">Image Organiser</h2>
          <div className="d-flex gap-2 ms-auto">
          </div>
        </Card.Header>

        {/* Path Breadcrumbs */}
        <Row>
          <Col>
            {renderCurrentPath()}
          </Col>
        </Row>

        <hr className="my-4" />
        <Card.Body className="p-3" style={{ overflowX: 'auto' }}>
          {/* Toolbar: Upload, Download, Add Folder */}
          <Row className="g-3" style={{ alignItems: 'flex-end' }}>
            {(user.role === 'artist' || user.role === 'admin') && (
              <Col xs="auto">
                <UploadFiles
                  ref={uploadComponent}
                  pathGetter={() => currentPath}
                  successCallback={() => updateContents()}
                />
              </Col>
            )}

            <Col xs="auto">
              <Button
                variant="outline-primary"
                href={`/api/images/download?path=${encodeURIComponent(currentPath ? currentPath : '')}`}
                download
              >
                Download Zip
              </Button>
            </Col>

            {(user.role === 'artist' || user.role === 'admin') && (
              <Col md>
                <Form.Label htmlFor="newFolderName" className="form-label" style={{ fontWeight: '500' }}>New Folder</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    id="newFolderName"
                    name="newFolderName"
                    value={newFolderName}
                    onChange={event => setNewFolderName(event.target.value)}
                    placeholder="Enter folder name..."
                    onKeyPress={(e) => e.key === 'Enter' && addFolder()}
                  />
                  <Button
                    variant="primary"
                    onClick={addFolder}
                    disabled={!newFolderName}
                  >
                    Add Folder
                  </Button>
                </InputGroup>
              </Col>
            )}
          </Row>

          {/* Content Grid */}
          {renderBody()}

        </Card.Body>
      </Card>
    </Container>
  );
};

export default ImageOrganiser;